import { useState, useRef, useMemo } from 'react';
import { useTour } from '../../../../TourContext.jsx'
import TourTooltip from '../../../../components2/TourTooltip';
import { createPortal } from "react-dom";

import { STEP_TEXT, TIP_COUNTS } from '../../../tours/tourCopy';
import { useActiveClassData, useProfile } from '../../../../components/state/hooks';
import { deriveTeacherPreset } from '../../../utils/teacherPreset.js';
import { formatYearDisplay } from '../../../../utils/strings';
import {
    Wrapper,
    Card,
    Title,
    Subtitle,
    State,
    Field,
    Label,
    Select,
    Arrow,
    PrimaryButton,
    Footnote,
} from './style';

// Customize Draft Feedback Modal Component
const CustomizeDraftFeedback = ({ onCreateDraft }) => {
    const {
        draftTourActive,
        draftTourStep,
        setDraftTourStep,
        setShowDraftFeedback
    } = useTour() || {};

    const [isOpen, setIsOpen] = useState(true);
    const cardRef = useRef(null); // reference for tooltip

    // These five fields were hardcoded to NSW / Year 12 / English /
    // Analytical / Extended Response. The popup's own subtitle promises the
    // feedback is generated from *your* draft settings, so showing someone
    // else's is the one thing it must not do — and by the time this renders
    // the user has already told us their state and year in the onboarding
    // dialogue. Derived from the same preset that chooses the demo draft
    // itself, so the popup and the draft behind it always agree.
    const { data: classes } = useActiveClassData(null, true);
    const { data: profile } = useProfile();
    const preset = useMemo(
        () => deriveTeacherPreset(classes || [], profile),
        [classes, profile]
    );

    const isHIGHLIGHTED = draftTourActive && draftTourStep === 2;

    if (!isOpen) return null;

    // The popup content
    const popupContent = (
        <Wrapper isTourActive={isHIGHLIGHTED}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Card
                    ref={cardRef}
                    style={
                        isHIGHLIGHTED
                            ? {
                                position: 'relative',
                                zIndex: 10002,
                                boxShadow: '0 0 0 4px white, 0 0 25px rgba(0,0,0,0.6)',
                                borderRadius: '16px',
                            }
                            : {}
                    }
                >
                    <Title>Customise your draft feedback</Title>
                    <Subtitle>Your feedback will be generated based on your draft settings</Subtitle>
                    <State>State: {preset.state}</State>

                    <Field>
                        <Label>Year</Label>
                        <Select>
                            {formatYearDisplay(preset.year)} <Arrow>▾</Arrow>
                        </Select>
                    </Field>

                    <Field>
                        <Label>Subject</Label>
                        <Select>
                            {preset.subject} <Arrow>▾</Arrow>
                        </Select>
                    </Field>

                    <Field>
                        <Label>Task Type</Label>
                        <Select disabled>
                            {preset.taskType} <Arrow>▾</Arrow>
                        </Select>
                    </Field>
                    <Field>
                        <Label>Response Type</Label>
                        <Select disabled>
                            Extended Response<Arrow>▾</Arrow>
                        </Select>
                    </Field>

                    {/* During onboarding the only sanctioned way forward is the
                        tour tooltip's "Next" (which closes this modal AND advances
                        the step). Clicking "Create Draft" directly only closes the
                        modal without advancing draftTourStep, stranding the user at
                        step 2 with the tour active and no visible UI (JED-1087).
                        Neutralise the button while the tour is active. */}
                    <PrimaryButton onClick={draftTourActive ? undefined : onCreateDraft}>Create Draft</PrimaryButton>

                    <Footnote>*Settings can also be changed after creating the draft</Footnote>
                </Card>

                {isHIGHLIGHTED && (
                    <TourTooltip
                        text={STEP_TEXT.draft[2]}
                        currentStep={draftTourStep}
                        tipCount={TIP_COUNTS.draft}
                        onNext={() => {
                            setShowDraftFeedback(false);
                            onCreateDraft();
                        }}
                        onBack={() => {
                            setShowDraftFeedback(false);
                        }}
                        onSkip={() => {
                            setShowDraftFeedback(false);
                        }}
                        reference={cardRef}  // target element
                        position="left"
                        style={{ width: '420px', zIndex: 10002 }}  // To display above overlay
                    />
                )}
            </div>
        </Wrapper>
    );

    // Render via portal to escape stacking context
    return createPortal(popupContent, document.body);
};

export default CustomizeDraftFeedback;